// ============================================================================
// RifiutiBox.jsx  —  FIX_RIFIUTI_IN_SICUREZZA_v1
//
// Componente autonomo per configurare la "Gestione rifiuti" (responsabile,
// tipologia, documentazione, misure tecniche) direttamente dentro la pagina
// "Sicurezza Azienda" (sezione 9), senza passare da Impostazioni.
//
// Usa la STESSA fonte dati del form in Impostazioni:
//   Firestore: impostazioni/rifiuti_default
//
// Include la protezione anti-sovrascrittura (stoScrivendo) per evitare che
// onSnapshot reinietti i dati mentre l'utente scrive.
//
// Componente isolato: stati e funzioni propri. Non tocca Gestionale.jsx.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { doc, onSnapshot, setDoc } from './firebase-shim';

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#22263a",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  info: "#378ADD",
  warning: "#D4537E",
  error: "#f06060",
};

const inputSt = {
  background: "#0f1117",
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "'DM Sans',sans-serif",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const RIF_DEFAULT_VUOTO = {
  responsabile: "",
  tipologia: { pericolosi: "", nonPericolosi: "", altro: "" },
  documentazione: {
    formulario:      { stato: "", note: "" },
    registroCarScar: { stato: "", note: "" },
    altro: "",
  },
  misure: {
    refettorio:  { stato: "", note: "" },
    spogliatoio: { stato: "", note: "" },
    baracche:    { stato: "", note: "" },
    ufficio:     { stato: "", note: "" },
    wcChimico:   { stato: "", note: "" },
  },
};

export default function RifiutiBox() {
  const [rifDefault, setRifDefault] = useState(RIF_DEFAULT_VUOTO);
  const [rifDefaultOriginale, setRifDefaultOriginale] = useState(RIF_DEFAULT_VUOTO);
  const [rifDefaultDirty, setRifDefaultDirty] = useState(false);
  const rifDefaultSaveTimer = useRef(null);
  // Protezione anti-sovrascrittura mentre si scrive
  const stoScrivendo = useRef(false);
  const fineScritturaTimer = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "impostazioni", "rifiuti_default"), snap => {
      if (stoScrivendo.current) return;
      if (snap.exists()) {
        const data = snap.data();
        const merged = {
          responsabile: data.responsabile || "",
          tipologia: { ...RIF_DEFAULT_VUOTO.tipologia, ...(data.tipologia || {}) },
          documentazione: { ...RIF_DEFAULT_VUOTO.documentazione, ...(data.documentazione || {}) },
          misure: { ...RIF_DEFAULT_VUOTO.misure, ...(data.misure || {}) },
        };
        setRifDefault(merged);
        setRifDefaultOriginale(merged);
        setRifDefaultDirty(false);
      }
    });
    return () => {
      try { unsub && unsub(); } catch(e){}
      if (fineScritturaTimer.current) clearTimeout(fineScritturaTimer.current);
    };
  }, []);

  const salvaRifDefault = async (data) => {
    try {
      await setDoc(doc(db, "impostazioni", "rifiuti_default"), { ...data, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ " + e.message); }
  };

  const segnaScrittura = () => {
    stoScrivendo.current = true;
    if (fineScritturaTimer.current) clearTimeout(fineScritturaTimer.current);
    fineScritturaTimer.current = setTimeout(() => { stoScrivendo.current = false; }, 1500);
  };

  const aggiornaRifResp = (valore) => {
    segnaScrittura();
    const nuovo = { ...rifDefault, responsabile: valore };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifTip = (campo, valore) => {
    segnaScrittura();
    const nuovo = { ...rifDefault, tipologia: { ...rifDefault.tipologia, [campo]: valore } };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifDoc = (key, campo, valore) => {
    segnaScrittura();
    let nuovaDoc;
    if (key === "altro") {
      nuovaDoc = { ...rifDefault.documentazione, altro: valore };
    } else {
      nuovaDoc = { ...rifDefault.documentazione, [key]: { ...rifDefault.documentazione[key], [campo]: valore } };
    }
    const nuovo = { ...rifDefault, documentazione: nuovaDoc };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifMis = (key, campo, valore) => {
    segnaScrittura();
    const nuovo = { ...rifDefault, misure: { ...rifDefault.misure, [key]: { ...rifDefault.misure[key], [campo]: valore } } };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const cardSt = { background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, padding:16 };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15 }}>🗑️ Gestione rifiuti — default Bertoc</div>
          <button className="btn btn-ghost btn-sm" disabled={!rifDefaultDirty}
            onClick={() => {
              if (!window.confirm("Annullare le modifiche e ripristinare l'ultimo salvataggio?")) return;
              setRifDefault(rifDefaultOriginale);
              setRifDefaultDirty(false);
              salvaRifDefault(rifDefaultOriginale);
            }}
            style={{ opacity: rifDefaultDirty ? 1 : 0.4 }}
          >↶ Annulla modifiche</button>
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted }}>
          Imposta i valori predefiniti proposti quando generi un POS. Salvataggio automatico.
        </div>
      </div>

      {/* Responsabile */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Responsabile della gestione dei rifiuti</div>
        <input style={inputSt} placeholder="es. Marco Bertocci"
          value={rifDefault.responsabile}
          onChange={e => aggiornaRifResp(e.target.value)} />
      </div>

      {/* TIPOLOGIA */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Tipologia di rifiuti prodotti
        </div>
        {[
          { key: "pericolosi",    label: "Pericolosi" },
          { key: "nonPericolosi", label: "Non pericolosi" },
        ].map(r => {
          const val = rifDefault.tipologia[r.key] || "";
          return (
            <div key={r.key} style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, fontWeight: 600, minWidth: 130 }}>{r.label}</div>
              {[
                { v: "si",          l: "SI" },
                { v: "no",          l: "NO" },
                { v: "da_definire", l: "Da definire" },
              ].map(o => (
                <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                  <input type="radio" name={`tip_${r.key}`} checked={val === o.v}
                    onChange={() => aggiornaRifTip(r.key, o.v)} />
                  <span>{o.l}</span>
                </label>
              ))}
            </div>
          );
        })}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Altro (specificare)</div>
          <input style={inputSt} placeholder="es. rifiuti speciali..."
            value={rifDefault.tipologia.altro}
            onChange={e => aggiornaRifTip("altro", e.target.value)} />
        </div>
      </div>

      {/* DOCUMENTAZIONE */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Documentazione presente in cantiere
        </div>
        {[
          { key: "formulario",      label: "Formulario" },
          { key: "registroCarScar", label: "Registro di carico e scarico" },
        ].map(r => {
          const val = rifDefault.documentazione[r.key] || { stato: "", note: "" };
          return (
            <div key={r.key} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{r.label}</div>
              <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
                {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                  <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                    <input type="radio" name={`doc_${r.key}`} checked={val.stato === o.v}
                      onChange={() => aggiornaRifDoc(r.key, "stato", o.v)} />
                    <span>{o.l}</span>
                  </label>
                ))}
              </div>
              <input style={inputSt} placeholder="Note (opz.)"
                value={val.note}
                onChange={e => aggiornaRifDoc(r.key, "note", e.target.value)} />
            </div>
          );
        })}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Altro (specificare)</div>
          <input style={inputSt} placeholder="es. registro storico..."
            value={rifDefault.documentazione.altro || ""}
            onChange={e => aggiornaRifDoc("altro", null, e.target.value)} />
        </div>
      </div>

      {/* MISURE */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Misure tecniche per la gestione dei rifiuti
        </div>
        {[
          { key: "refettorio",  label: "Rifiuti dal locale Refettorio/mensa" },
          { key: "spogliatoio", label: "Rifiuti dallo spogliatoio" },
          { key: "baracche",    label: "Pulizia servizi e baracche" },
          { key: "ufficio",     label: "Ufficio in cantiere" },
          { key: "wcChimico",   label: "Altri rifiuti (WC chimico)" },
        ].map(r => {
          const val = rifDefault.misure[r.key] || { stato: "", note: "" };
          return (
            <div key={r.key} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{r.label}</div>
              <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
                {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                  <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                    <input type="radio" name={`mis_${r.key}`} checked={val.stato === o.v}
                      onChange={() => aggiornaRifMis(r.key, "stato", o.v)} />
                    <span>{o.l}</span>
                  </label>
                ))}
              </div>
              <input style={inputSt} placeholder='Misure tecniche / note (es. "Conferimento al servizio locale")'
                value={val.note}
                onChange={e => aggiornaRifMis(r.key, "note", e.target.value)} />
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: theme.textDim, textAlign: "center", padding: "8px 0" }}>
        {rifDefaultDirty ? "💾 Salvataggio in corso..." : "✓ Tutti i dati salvati"}
      </div>
    </div>
  );
}

